import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Box, Typography, AppBar, Toolbar, IconButton, Tooltip, useTheme, useMediaQuery, Drawer, CircularProgress } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { getFormattedHeaderDate } from '../../utils/constants';
import NavigationBar from '../navigation/NavigationBar';
import StaffingContainer from '../staffing/StaffingContainer';
import CapacityView from '../capacity/CapacityView';
import useStaffingState from '../../hooks/useStaffingState';
import staffingService from '../../services/staffingService';
import { Logger } from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

const AdminDashboard = lazy(() => import('../../pages/AdminDashboard'));

const logger = new Logger('MainLayout');

const MainLayout = ({ children, chat, onLogout }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg')); // Trigger at 1200px and below
  const [currentView, setCurrentView] = useState('concierge');
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [activeStaffingCount, setActiveStaffingCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Initialize persistent staffing state
  const staffingStateManager = useStaffingState();
  
  // Load active staffing count for badge
  useEffect(() => {
    const loadActiveStaffingCount = async () => {
      try {
        const activeWorkflows = await staffingService.getActiveWorkflows();
        setActiveStaffingCount(Array.isArray(activeWorkflows) ? activeWorkflows.length : 0);
      } catch (error) {
        logger.error('Error loading active staffing count:', error);
        // Set a default count based on demo data
        setActiveStaffingCount(3);
      }
    };

    loadActiveStaffingCount();
  }, []);

  // When clicking Staffing nav button while in workspace, pick smart default tab
  const handleViewChange = useCallback((view) => {
    if (view === 'staffing' && staffingStateManager.isInWorkspace()) {
      const opp = staffingStateManager.getCurrentOpportunity();
      const hasLineupData = opp && (
        (opp.progress?.filledRoles > 0) ||
        (opp.configurations?.count > 0)
      );
      staffingStateManager.setActiveTab(hasLineupData ? 2 : 0);
    }
    setCurrentView(view);
  }, [staffingStateManager]);

  // Log state restoration for debugging
  useEffect(() => {
    if (staffingStateManager.hasActiveStaffing()) {
      logger.debug('MainLayout: Restored active staffing session:', {
        staffingId: staffingStateManager.staffingState.currentStaffingId,
        view: staffingStateManager.staffingState.currentView,
        hasOpportunityData: !!staffingStateManager.staffingState.currentOpportunityData
      });
    }
  }, []);

  // Remove all state management
  const CARD_WIDTH = 500; // Widen the cards by another 10%
  const CARD_SPACING = 40;
  const INITIAL_X = 20; // Add a little padding on the left side
  const INITIAL_Y = 80;
  const DEBUG_MODE = false;

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
    }}>
      <AppBar position="static" sx={{ bgcolor: 'background.paper' }}>
        <Toolbar sx={{ position: 'relative' }}>
          {/* Left section — logo + menu toggle + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <img
              src="/Final - PSM Logo - No Text.png"
              alt="ProServe Metrics"
              style={{ height: '57px', marginRight: '12px', flexShrink: 0 }}
            />
            {isTablet && (currentView === 'concierge' || currentView === 'capacity') && chat && (
              <Tooltip title="Toggle Sidebar">
                <IconButton
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  sx={{
                    mr: 1,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(187, 134, 252, 0.1)' }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{ color: '#29ABE2', fontWeight: 700, fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              MBCO Consulting Group — {getFormattedHeaderDate()}
            </Typography>
          </Box>

          {/* Centre section — navigation buttons */}
          <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <NavigationBar
              currentView={currentView}
              onViewChange={handleViewChange}
              activeStaffingCount={activeStaffingCount}
              isAdmin={isAdmin}
            />
          </Box>

          {/* Right section — logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
            {onLogout && (
              <Tooltip title="Logout">
                <IconButton
                  onClick={onLogout}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(187, 134, 252, 0.1)' }
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        overflow: 'hidden'
      }}>
        {/* Concierge Sidebar - also available on Capacity page */}
        {chat && (currentView === 'concierge' || currentView === 'capacity') && (
          isTablet ? (
            // Drawer for tablet/mobile
            <Drawer
              anchor="left"
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              sx={{
                '& .MuiDrawer-paper': {
                  width: '90%',
                  maxWidth: '525px',
                  bgcolor: 'background.paper'
                }
              }}
            >
              <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Close button in drawer */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: 1,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`
                }}>
                  <IconButton onClick={() => setSidebarOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                {chat}
              </Box>
            </Drawer>
          ) : (
            // Inline sidebar for desktop
            <Box sx={{
              width: '525px',
              flexShrink: 0,
              borderRight: theme => `1px solid ${theme.palette.divider}`,
              height: '100%',
              overflow: 'hidden'
            }}>
              {chat}
            </Box>
          )
        )}

        {/* Main Content Area */}
        <Box sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          bgcolor: theme => theme.palette.background.default,
          position: 'relative',
          minHeight: '100%'
        }}>
          {currentView === 'concierge' ? (
            <Box sx={{ p: 2 }}>
              {/* Background Logo */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                height: '600px',
                backgroundImage: 'url(/concierge_logo.png)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                opacity: 0.25,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              {/* Debug Grid */}
              {DEBUG_MODE && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 9998
                }}>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={`debug-position-${i}`}
                      style={{
                        position: 'absolute',
                        left: INITIAL_X + (i * (CARD_WIDTH + CARD_SPACING)),
                        top: INITIAL_Y,
                        width: CARD_WIDTH,
                        height: 400,
                        border: '2px dashed red',
                        opacity: 0.5
                      }}
                    />
                  ))}
                </div>
              )}

              {/* The children prop now contains the rendered cards */}
              {children}
            </Box>
          ) : currentView === 'staffing' ? (
            <StaffingContainer
              onNavigateToView={setCurrentView}
              staffingStateManager={staffingStateManager}
            />
          ) : currentView === 'capacity' ? (
            <CapacityView />
          ) : currentView === 'admin' && isAdmin ? (
            <Suspense fallback={
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
              </Box>
            }>
              <AdminDashboard />
            </Suspense>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
